import * as mobxReact from 'mobx-react';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import OECheckbox, { IProps as CheckboxProps } from '../../../../../global/oe-checkbox/oe-checkbox';
import { IProps as RichTextEditorProps, RichTextEditor } from '../../../../../global/rich-text-editor';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { configWithImageUpload } from '../../../../../global/rich-text-editor/CKEditor.config';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import DefaultButton from '../../../../../global/default-button/default-button';
import { ConnectionFormVM, FormFieldsProps, FormInputType } from './connection-form.viewmodel';
import { ConnectionType, IConnectionDetailsDTO, KnovioLabel } from '../../../../../../api/connections.dao';
import { ConfirmDeletePopover } from '../../../shared/connections-list/_connection-delete-confirm-popover';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../global/text/text';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';
import KnovioPreviewLinkPopover from '../../knovio-preview-link-popover/knovio-preview-link-popover';
import { cancelText, submitText } from '../../../../../../constants/common-labels.constants';
import { ConnectionFormFooterOptionsButton } from './_connection-form-footer/options-button/connection-form-footer-options-btn';
import { SyncSpeakersModal } from './_sync-speakers/sync-speakers-modal';
import './connections-form.scss';
import { ConnectionFormBody } from './connection-form-body/connection-form-body';

type ConnectionItemFieldProps = FormFieldsProps & {
    onChange?: (e: React.SyntheticEvent) => void;
    onBlur?: () => void;
    onClick?: () => void;
    className?: string;
    checked?: boolean;
    connectionType?: ConnectionType;
};

const ConnectionItemFormField: React.FunctionComponent<ConnectionItemFieldProps> =
    ({
        name,
        value,
        fieldType,
        readOnly,
        onChange,
        options,
        onBlur,
        onClick,
        toggleValues,
        toggleInfoMessage,
        connectionType,
    }: ConnectionItemFieldProps): JSX.Element => {
        const _props = {
            id: name, value: value === ConnectionType.Knovio ? KnovioLabel.OEStream : value, disabled: readOnly, className: 'oe-form-field__control',
            onChange: readOnly ? undefined : e => onChange(e.target.value),
            onBlur: readOnly ? undefined : () => onBlur(),
            onClick: onClick
        };

        switch (fieldType) {
            case FormInputType.Checkbox:
                const checkboxProps = {
                    checked: value, isDisabled: readOnly, classNames: 'oe-checkbox--show-before-label',
                    onChange: readOnly ? undefined : e => onChange(e.target.checked)
                } as CheckboxProps;

                return React.createElement(OECheckbox, checkboxProps);

            case FormInputType.Select:
                const optionItems = options.map(option => {
                    return option != null && typeof option === 'object'
                        ? <option key={String(option.value)} value={option.value}>{option.label || option.value || ''}</option>
                        : <option key={String(option)}>{option || ''}</option>;
                });

                return React.createElement(fieldType, _props, optionItems);

            case FormInputType.RichTextInput:
                const richTextInputProps = {
                    ..._props,
                    value: _props.value || '',
                    className: 'oe-form-field__control p-0px m-0px',
                    config: configWithImageUpload,
                    onChange: readOnly ? undefined : (e, editor) => onChange(editor.getData())
                } as RichTextEditorProps;

                return React.createElement(RichTextEditor, richTextInputProps);

            case FormInputType.Toggle:
                const itemValue = _props.value as boolean;
                return (
                    <div className='flex center'>
                        <OEToggle
                            onClick={_props.onClick}
                            isDisabled={_props.disabled}
                            isSelected={itemValue}
                        />
                        <Text
                            className={'m-left-8px font-size-12px'}
                            message={itemValue ? toggleValues.enabled.value : toggleValues.disabled.value}
                            id={itemValue ? toggleValues.enabled.id : toggleValues.disabled.id}
                        />
                        {toggleInfoMessage &&
                            <InfoPopover className='m-left-auto'>
                                <div>
                                    {toggleInfoMessage[connectionType] || toggleInfoMessage}
                                </div>
                            </InfoPopover>
                        }
                    </div>
                );

            default:
                return React.createElement(fieldType, {
                    ..._props,
                    value: _props.value || ''
                });
        }
};

const ConnectionFormPresentation = mobxReact.observer((props: { vm: ConnectionFormVM }) => {
    // TODO: move code related to Unique Joining Link to the viewmodel
    const [deletePopoverConnection, setDeletePopoverConnection] = useState<IConnectionDetailsDTO | null>(null);
    const formVM = props.vm;
    const formData = formVM.getConnectionFormDataVM();
    const connectionManagmentPermission: boolean = hasPermission(Permission.CONNECTIONS_MANAGEMENT);
    const renderOptionsButton: boolean = connectionManagmentPermission
        && formData?.type === ConnectionType.UniqueJoiningLink
        && !!formData.meetingConnectionId;
    const renderKnovioPreviewLinkPopover = formData?.type === ConnectionType.Knovio
        && formVM.isEdit()
        && formVM.knovioPreviewLinkPopoverVM
        && formVM.knovioPreviewLinkPopoverVM.isPopoverOpened
        && formVM.knovioPreviewLinkPopoverVM.isInstanceReady
        && hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION);

    if (!formData) {
        return <CommonPopupBodyFooterLayout body={<div className='oe-form meeting-form'>loading connection data...</div>}/>;
    }

    return (
        <CommonPopupBodyFooterLayout
            body={
                <>
                    <ConnectionFormBody formVM={formVM}/>
                    {renderOptionsButton &&
                        <ConfirmDeletePopover
                            deletePopoverConnection={deletePopoverConnection}
                            setDeletePopoverConnection={setDeletePopoverConnection}
                            handleOnDelete={formVM.deleteConnection}
                        />
                    }
                    <SyncSpeakersModal vm={formVM.syncSpeakersVM} />
                    {renderKnovioPreviewLinkPopover && <KnovioPreviewLinkPopover vm={formVM.knovioPreviewLinkPopoverVM}/>}
                </>
            }
            footer={
                <>
                    {formVM.isSaving() && <span className='oe-popup-layout__footer-status-area'>
                          <FormattedMessage
                              id='oe.connections.connectionDataIsLoading'
                              description='Preloader message text'
                              defaultMessage='Connection data is loading, please wait{suffix}'
                              values={{
                                  suffix: '...'
                              }}
                          />
                      </span>}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitText(formVM.isEdit())}
                            modifier={['fat', 'confirm-safe']}
                            onClick={formVM.formSubmitAction}
                            disabled={formVM.isSaving() || formVM.loadWebinarDetails}
                        />
                        {!formVM.isEdit() && formVM.isAutomaticallyCreatedZoomConnection() ?
                            <DefaultButton
                                text={cancelText()}
                                onClick={formVM.onCloseZoomConnection}
                                modifier={['cancel', 'fat']}
                            /> :
                            <DefaultCloseSubWindowButton
                                text={cancelText()}
                            />
                        }
                        <ConnectionFormFooterOptionsButton
                            connectionType={formData?.type}
                            shouldRender={formVM.shouldShowOptionsButton()}
                            knovioConnectionMoreOptions={formVM.getKnovioConnectionOptions()}
                            onDeleteConnectionAction={setDeletePopoverConnection.bind(this, formData.toApiDTO())}
                        />
                    </div>
                </>
            }
        />
    );
});

export { ConnectionFormPresentation, ConnectionItemFormField };

